import { useEffect, useRef } from "react";

import { FirebaseAuth } from "../../../utils/firebase";
import { CbhFeatureFlag, useCbhFlag } from "../../FeatureFlags";
import { APP_V2_APP_EVENTS, logError, logEvent } from "../../lib/analytics";
import {
  IDENTITY_PROVIDER,
  isNetworkFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  setLoginIdentityProvider,
} from "../loginRum";
import { exchangeFirebaseTokenForCognitoSessionIfNeeded } from "./firebaseTokenExchange";

/**
 * Ensures that a Cognito session exists for the current user when the
 * `2026-03-admin-enable-cognito-in-api-calls` flag is enabled.
 *
 * Users who were already logged in via Firebase before the migration flag was
 * turned on have a valid Firebase session but no Cognito tokens in
 * localStorage. When the flag is enabled, API helpers try to send a Cognito
 * token, fail to find one, and silently fall back to Firebase — which is why
 * most users still appear as "Firebase" on the Datadog dashboard.
 *
 * This hook runs once when the flag first becomes `true` and, if no Cognito
 * session exists, exchanges the current Firebase ID token for a Cognito
 * session in the background. Failures are logged but never block the user.
 *
 * Note: The primary exchange now happens in `init.ts`'s `onLoggedIn` callback
 * (which fires after Firebase's `onIdTokenChanged`). This hook serves as a
 * defense-in-depth fallback for edge cases where `onLoggedIn` may not fire
 * (e.g., if `initCognitoSession` returned `true` but the Cognito session is
 * stale/mismatched).
 *
 * Mount this hook once, high in the component tree (next to
 * `useSyncCognitoApiTokenFlag`).
 */
export function useEnsureCognitoSession(): void {
  const hasAttempted = useRef(false);

  const isCognitoEnabled = useCbhFlag(CbhFeatureFlag.ADMIN_ENABLE_COGNITO_IN_API_CALLS, {
    defaultValue: false,
  });

  useEffect(() => {
    if (!isCognitoEnabled || hasAttempted.current) {
      return;
    }

    const firebaseUser = FirebaseAuth.currentUser;
    const email = firebaseUser?.email;
    if (!email) {
      // Firebase session not yet available. Since the dependency array is
      // [isCognitoEnabled], this effect won't automatically re-run when
      // Firebase becomes available. However, the primary exchange in
      // init.ts onLoggedIn handles the timing reliably. This hook is only
      // defense-in-depth for the edge case where the Firebase user is
      // already available when the flag first becomes true.
      return;
    }

    hasAttempted.current = true;

    void (async () => {
      try {
        const firebaseIdToken = await firebaseUser.getIdToken();

        const exchanged = await exchangeFirebaseTokenForCognitoSessionIfNeeded({
          email,
          firebaseIdToken,
        });
        if (exchanged) {
          // Only tag identityProvider once we know an actual fallback exchange
          // happened — otherwise we'd taint subsequent telemetry for users
          // whose Cognito session was already valid.
          setLoginIdentityProvider(IDENTITY_PROVIDER.FIREBASE_FALLBACK);
          logEvent(APP_V2_APP_EVENTS.PASSWORD_LOGIN_COGNITO_SESSION_SUCCESS, {
            source: "session-restore",
            outcome: LOGIN_OUTCOME.SUCCESS,
            identityProvider: IDENTITY_PROVIDER.FIREBASE_FALLBACK,
          });
        }
      } catch (error: unknown) {
        setLoginIdentityProvider(IDENTITY_PROVIDER.FIREBASE_FALLBACK);
        logError(APP_V2_APP_EVENTS.PASSWORD_LOGIN_COGNITO_SESSION_FAILURE, {
          error,
          metadata: {
            source: "session-restore",
            outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
            reason: isNetworkFailure(error)
              ? LOGIN_REASON.NETWORK_FAILURE
              : LOGIN_REASON.FIREBASE_EXCHANGE_FAILED,
            identityProvider: IDENTITY_PROVIDER.FIREBASE_FALLBACK,
          },
        });
      }
    })();
  }, [isCognitoEnabled]);
}
